import {
    collection,
    doc,
    getDoc,
    setDoc,
    addDoc,
    onSnapshot,
    query,
    orderBy,
    updateDoc,
    where,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { ConsultationMessage, ConsultationThread } from '../types';

export function buildConsultationThreadId(clientId: string, consultantId: string): string {
    return `${clientId}_${consultantId}`;
}

export async function ensureConsultationThread(input: {
    clientId: string;
    consultantId: string;
    clientUid: string;
    consultantUid: string;
    householdId?: string;
}): Promise<ConsultationThread> {
    const threadId = buildConsultationThreadId(input.clientId, input.consultantId);
    const ref = doc(db, 'consultationThreads', threadId);
    const snap = await getDoc(ref);
    const now = new Date().toISOString();

    if (snap.exists()) {
        return { ...(snap.data() as Omit<ConsultationThread, 'id'>), id: threadId };
    }

    const thread: Omit<ConsultationThread, 'id'> = {
        clientId: input.clientId,
        consultantId: input.consultantId,
        clientUid: input.clientUid,
        consultantUid: input.consultantUid,
        participantIds: [input.clientUid, input.consultantUid],
        householdId: input.householdId,
        status: 'open',
        lastMessageAt: now,
        createdAt: now,
    };
    await setDoc(ref, thread);
    return { ...thread, id: threadId };
}

export async function sendConsultationMessage(
    threadId: string,
    from: 'client' | 'consultant',
    text: string,
    senderUid: string,
): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;
    const now = new Date().toISOString();
    await addDoc(collection(db, 'consultationThreads', threadId, 'messages'), {
        from,
        text: trimmed,
        createdAt: now,
        senderUid,
    } satisfies Omit<ConsultationMessage, 'id'>);
    await updateDoc(doc(db, 'consultationThreads', threadId), {
        lastMessageAt: now,
        status: 'open',
        lastMessageFrom: from,
    });
}

export function threadHasUnreadForConsultant(thread: ConsultationThread): boolean {
    if (thread.lastMessageFrom !== 'client') return false;
    if (!thread.consultantReadAt) return true;
    return thread.consultantReadAt < thread.lastMessageAt;
}

export function threadHasUnreadForClient(thread: ConsultationThread): boolean {
    if (thread.lastMessageFrom !== 'consultant') return false;
    if (!thread.clientReadAt) return true;
    return thread.clientReadAt < thread.lastMessageAt;
}

export async function markConsultationThreadRead(threadId: string): Promise<void> {
    const now = new Date().toISOString();
    await updateDoc(doc(db, 'consultationThreads', threadId), {
        consultantReadAt: now,
    });
}

export async function markConsultationThreadReadForClient(threadId: string): Promise<void> {
    const now = new Date().toISOString();
    await updateDoc(doc(db, 'consultationThreads', threadId), {
        clientReadAt: now,
    });
}

export function subscribeConsultationMessages(
    threadId: string,
    onMessages: (messages: ConsultationMessage[]) => void,
): () => void {
    const q = query(
        collection(db, 'consultationThreads', threadId, 'messages'),
        orderBy('createdAt', 'asc'),
    );
    return onSnapshot(q, (snap) => {
        onMessages(snap.docs.map((d) => ({ ...(d.data() as Omit<ConsultationMessage, 'id'>), id: d.id })));
    });
}

export function subscribeClientConsultationThreads(
    clientId: string,
    onThreads: (threads: ConsultationThread[]) => void,
): () => void {
    const q = query(collection(db, 'consultationThreads'), where('clientId', '==', clientId));
    return onSnapshot(q, (snap) => {
        onThreads(snap.docs.map((d) => ({ ...(d.data() as Omit<ConsultationThread, 'id'>), id: d.id })));
    });
}

export function subscribeConsultantConsultationThreads(
    consultantId: string,
    onThreads: (threads: ConsultationThread[]) => void,
): () => void {
    const q = query(collection(db, 'consultationThreads'), where('consultantId', '==', consultantId));
    return onSnapshot(q, (snap) => {
        onThreads(snap.docs.map((d) => ({ ...(d.data() as Omit<ConsultationThread, 'id'>), id: d.id })));
    });
}
